import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../../ui/utils";

export type CellAlign = "left" | "right" | "center";

const ALIGN: Record<CellAlign, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

// Px minimums: badges/numbers never wrap/clip; wordMinWidth floors a column at its longest word.
export function badgeMinWidth(chars: number): number {
  return Math.min(148, Math.max(82, Math.round(chars * 7 + 34)));
}

export function typeMinWidth(chars: number): number {
  return Math.min(150, Math.max(78, Math.round(chars * 6.5 + 34)));
}

export function numericMinWidth(chars: number): number {
  return Math.min(140, Math.max(58, Math.round(chars * 7 + 30)));
}

const TITLE_COMFORT = 288;
const TEXT_COMFORT = 168;
const FLEX_MIN = 48;
// If the title column cannot get this width the table stacks into cards rather than truncating.
const TITLE_STACK_MIN = 132;

export function wordMinWidth(chars: number): number {
  return Math.min(200, Math.max(FLEX_MIN, Math.round(chars * 7.2 + 26)));
}

export interface AssistantTableColumn {
  align?: CellAlign;
  noWrap?: boolean;
  minPx?: number;
  grow?: number;
  isTitle?: boolean;
  cellClassName?: string;
}

const SURFACE = "border-border/50 bg-muted/30 dark:border-white/[0.06] dark:bg-white/[0.02]";

function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

// No inner vertical scroll - the chat panel owns vertical scrolling.
export function AssistantTable({
  columns,
  header,
  rows,
}: {
  columns: AssistantTableColumn[];
  header: ReactNode[];
  rows: ReactNode[][];
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const colCount = columns.length;

  const isFlex = (i: number) => (columns[i]?.grow ?? 0) > 0;
  const fixedMinOf = (i: number) => columns[i]?.minPx ?? 96;
  const flexFloorOf = (i: number) => Math.max(FLEX_MIN, columns[i]?.minPx ?? 0);
  const comfortOf = (i: number) =>
    isFlex(i)
      ? Math.max(columns[i]?.isTitle ? TITLE_COMFORT : TEXT_COMFORT, flexFloorOf(i))
      : fixedMinOf(i);

  const flexIdx = columns.map((_, i) => i).filter(isFlex);
  const fixedTotal = columns.reduce((s, _, i) => s + (isFlex(i) ? 0 : fixedMinOf(i)), 0);
  const comfortTotal = columns.reduce((s, _, i) => s + comfortOf(i), 0);
  const flexReserveOf = (i: number) =>
    Math.max(columns[i]?.isTitle ? TITLE_STACK_MIN : FLEX_MIN, flexFloorOf(i));
  const sumMin = fixedTotal + flexIdx.reduce((s, i) => s + flexReserveOf(i), 0);

  const stacked = width > 0 && colCount > 1 && width < sumMin;

  if (stacked) {
    return (
      <div ref={ref} className="my-1.5">
        <StackedTable columns={columns} header={header} rows={rows} />
      </div>
    );
  }

  const measured = width > 0;
  let pxWidths = columns.map((_, i) => comfortOf(i));
  let tableWidth = comfortTotal;
  if (measured) {
    if (width >= comfortTotal) {
      pxWidths = columns.map((_, i) => comfortOf(i));
      tableWidth = comfortTotal;
    } else {
      const growTotal = flexIdx.reduce((s, i) => s + (columns[i].grow ?? 1), 0) || 1;
      const flexFloorTotal = flexIdx.reduce((s, i) => s + flexFloorOf(i), 0);
      const extra = Math.max(0, width - fixedTotal - flexFloorTotal);
      pxWidths = columns.map((c, i) =>
        isFlex(i) ? flexFloorOf(i) + extra * ((c.grow ?? 1) / growTotal) : fixedMinOf(i),
      );
      tableWidth = width;
    }
  }
  const pct = (i: number) => (pxWidths[i] / (tableWidth || 1)) * 100;

  const cellCls = (i: number) =>
    cn(
      "px-3 py-1.5 align-top",
      columns[i]?.noWrap ? "whitespace-nowrap" : "break-words",
      ALIGN[columns[i]?.align ?? "left"],
      columns[i]?.cellClassName,
    );

  return (
    <div ref={ref} className="my-1.5">
      <div
        className={cn(
          "scrollbar-neutral max-w-full overflow-x-auto overscroll-x-contain rounded-xl border",
          SURFACE,
        )}
        style={measured ? { width: tableWidth } : undefined}
      >
        <table className="w-full table-fixed border-separate border-spacing-0 text-[0.75rem] leading-snug text-foreground">
          <colgroup>
            {columns.map((_, i) => (
              <col key={i} style={measured ? { width: `${pct(i)}%` } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {Array.from({ length: colCount }, (_, i) => (
                <th
                  key={i}
                  scope="col"
                  className={cn(
                    cellCls(i),
                    "border-b border-border/60 py-2 text-[0.6875rem] font-semibold tracking-wide text-muted-foreground",
                    "bg-secondary/95 dark:border-white/10 dark:bg-[#1b1830]/95",
                  )}
                >
                  {header[i] ?? null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr
                key={r}
                className={cn(
                  "transition-colors last:[&>td]:border-b-0",
                  "even:bg-muted/20 hover:bg-primary/[0.04]",
                  "dark:even:bg-white/[0.02] dark:hover:bg-white/[0.04]",
                )}
              >
                {Array.from({ length: colCount }, (_, c) => (
                  <td key={c} className={cn(cellCls(c), "border-b border-border/25")}>
                    {row[c] ?? null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StackedTable({
  columns,
  header,
  rows,
}: {
  columns: AssistantTableColumn[];
  header: ReactNode[];
  rows: ReactNode[][];
}) {
  const titleIdx = Math.max(
    0,
    columns.findIndex((c) => c.isTitle),
  );
  return (
    <div className={cn("rounded-xl border", SURFACE)}>
      {rows.map((row, r) => (
        <div
          key={r}
          className={cn("px-3 py-2.5", r > 0 && "border-t border-border/40 dark:border-white/10")}
        >
          <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1.5">
            {columns.map((_, i) => (
              <Fragment key={i}>
                <dt className="whitespace-nowrap text-[0.6875rem] font-medium tracking-wide text-muted-foreground">
                  {header[i] ?? null}
                </dt>
                <dd
                  className={cn(
                    "min-w-0 break-words text-[0.75rem] text-foreground",
                    i === titleIdx ? "font-semibold" : "font-medium",
                  )}
                >
                  {row[i] ?? null}
                </dd>
              </Fragment>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}
